import { useEffect, useState, useRef } from "react";
import "./css/AccountModal.css";
import HeroSvg from "../Layout/HeroSvg";
import SideMenu from "./SideMenu";
import { useDispatch } from "react-redux";
import {
	setActiveButton,
	setShowAccountModal,
} from "../../redux/features/footerMenuSlice";
import { useNavigate } from "react-router-dom";
import { message, Switch } from "antd";
import { SponsorSvg } from "../AllSvgModulesExport/LoginSvg";
import {
	ChevronRightIcon,
	LogoutIcon,
} from "./AccountModalSvg";
import { userExist, userNotExist } from "../../redux/features/userSlice";
import { completeRegistration, getMarketingConsent, updateMarketingConsent } from "../../services/user.service";
import apiClient from "../../services/apiClient.js"; // Assuming you have this

const AccountModal = ({ show, user }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const fileInputRef = useRef(null);
	const [sideMenu, setSideMenu] = useState(false);
	const [tab, setTab] = useState(1);
	const [form, setForm] = useState({});
	const [loading, setLoading] = useState(false);
	const [uploadingPicture, setUploadingPicture] = useState(false);
	const [profilePicture, setProfilePicture] = useState(null);
	const [marketingConsent, setMarketingConsent] = useState(false);
	const [consentLoading, setConsentLoading] = useState(true);

	useEffect(() => {
		if (user) {
			console.log(user);
			setForm(user);
			setProfilePicture(user?.profilePicture);
		}
	}, [user]);

	useEffect(() => {
		if (!show || !user) return;
		setConsentLoading(true);
		getMarketingConsent()
			.then((data) => setMarketingConsent(data?.marketingConsent === true))
			.catch((error) => console.log(error))
			.finally(() => setConsentLoading(false));
	}, [show, user]);

	const capitalizeFirstLetter = (str) => {
		if (!str) return "";

		return str
			.split(" ")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(" ");
	};

	const getFirstLetter = (name) => {
		if (!name) return "";
		return name.charAt(0).toUpperCase();
	};

	if (!show) {
		return null;
	}

	const onClose = () => {
		dispatch(setShowAccountModal(false));
		dispatch(setActiveButton("home"));
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setForm((prevForm) => ({
			...prevForm,
			[name]: value,
		}));
	};

	// Handle profile picture selection
	const handleProfilePictureClick = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	// Handle profile picture file change
	const handleProfilePictureChange = async (e) => {
		const file = e.target.files[0];
		if (!file) return;

		// Validate file type
		const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
		if (!allowedTypes.includes(file.type)) {
			message.error('Please select a valid image file (JPEG, PNG, or GIF)');
			return;
		}

		// Validate file size (5MB max)
		if (file.size > 5 * 1024 * 1024) {
			message.error('Image size should be less than 5MB');
			return;
		}

		setUploadingPicture(true);

		try {
			const formData = new FormData();
			formData.append('image', file);

			const response = await apiClient.post('/user/profile-picture', formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
					Authorization: "Bearer " + localStorage.getItem("token"),
				},
			});

			if (response.data) {
				setProfilePicture(response.data.profilePicture);
				message.success('Profile picture updated successfully!');
				
				// Update user in Redux store
				const updatedUser = { ...user, profilePicture: response.data.profilePicture };
				dispatch(userExist(updatedUser));
			}
		} catch (error) {
			console.error('Error uploading profile picture:', error);
			message.error(error.response?.data?.message || 'Failed to upload profile picture');
		} finally {
			setUploadingPicture(false);
		}
	};

	const handleConsentChange = async (checked) => {
		setConsentLoading(true);
		try {
			const data = await updateMarketingConsent(checked);
			setMarketingConsent(data?.marketingConsent ?? checked);
			message.success(checked ? "You're subscribed to email offers" : "You've been unsubscribed from email offers");
		} catch (error) {
			message.error("Could not update your email preference");
		} finally {
			setConsentLoading(false);
		}
	};

	const handleUpdate = async (e) => {
		e.preventDefault();
		setLoading(true);

		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

		if (form?.email === "" || form?.name === "") {
			setLoading(false);
			return message.error(
				`Please Enter ${form?.name === "" ? "your name" : "your email"}`
			);
		}
		if (!emailRegex.test(form?.email)) {
			setLoading(false);
			return message.error("Please enter a valid email address");
		}
		if (form?.name?.length < 5) {
			setLoading(false);
			return message.error("Name should be more than 5 characters");
		}

		try {
			// Create FormData for profile update
			const formData = new FormData();
			formData.append('name', form.name);
			if (form.currentPassword) {
				formData.append('currentPassword', form.currentPassword);
			}
			if (form.newPassword) {
				formData.append('newPassword', form.newPassword);
			}

			const response = await apiClient.put('/user/profile', formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
					Authorization: "Bearer " + localStorage.getItem("token"),
				},
			});

			if (response.data) {
				setForm({ ...form, currentPassword: "", newPassword: "" });
				message.success(response.data.message);
				
				// Update user in Redux store with new data
				const updatedUser = { 
					...user, 
					name: response.data.user.name,
					email: response.data.user.email,
					phone: response.data.user.phone,
					profilePicture: response.data.user.profilePicture || profilePicture
				};
				dispatch(userExist(updatedUser));
			}
		} catch (error) {
			console.error('Error updating profile:', error);
			message.error(error.response?.data?.message || 'Failed to update profile');
		} finally {
			setLoading(false);
		}
	};

	const states = [
		"Andhra Pradesh",
		"Arunachal Pradesh",
		"Assam",
		"Bihar",
		"Chhattisgarh",
		"Goa",
		"Gujarat",
		"Haryana",
		"Himachal Pradesh",
		"Jharkhand",
		"Karnataka",
		"Kerala",
		"Madhya Pradesh",
		"Maharashtra",
		"Manipur",
		"Meghalaya",
		"Mizoram",
		"Nagaland",
		"Odisha",
		"Punjab",
		"Rajasthan",
		"Sikkim",
		"Tamil Nadu",
		"Telangana",
		"Tripura",
		"Uttar Pradesh",
		"Uttarakhand",
		"West Bengal",
	];

	const handleButtonClick = (path) => {
		dispatch(setShowAccountModal(false));
		dispatch(setActiveButton("more"));
		navigate(path);
	};

	const handleLogout = () => {
		const userConfirmed = window.confirm("Are you sure you want to logout?");

		if (userConfirmed) {
			dispatch(userNotExist());
			localStorage.clear();
			message.success("Logout Successful");
			navigate("/");
			dispatch(setActiveButton("home"));
			dispatch(setShowAccountModal(false));
		}
	};

	return (
		<>
			<div className={`account-modal-container d-lg-none d-md-none `}>
				<HeroSvg />
				<div className="account-modal-container-content">
					<div className="top-left-content">
						<div className="box-1 center" onClick={onClose}>
							<svg
								width="16"
								height="16"
								fill="currentColor"
								className="bi bi-chevron-left"
								viewBox="0 0 16 16"
							>
								<path
									fillRule="evenodd"
									d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"
								/>
							</svg>
						</div>
						<h2 className="mb-0">My Account</h2>
					</div>
					<div
						className="top-right-content box-2 center"
						onClick={() => setSideMenu(!sideMenu)}
					>
						<svg
							width="18"
							height="18"
							fill="currentColor"
							className="bi bi-list text-white"
							viewBox="0 0 16 16"
						>
							<path
								fillRule="evenodd"
								d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5"
							/>
						</svg>
					</div>
				</div>

				<div className="account-modal-bottom-container px-3">
					<div className="account-modal-bottom-content px-3 pb-3">
						<div className="account-modal-bottom-profile-content center flex-column">
							{/* Profile Picture with Upload Functionality */}
							<div className="profile-circle center position-relative" onClick={handleProfilePictureClick} style={{ cursor: 'pointer' }}>
								{profilePicture ? (
									<img 
										src={profilePicture} 
										alt="Profile" 
										style={{
											width: '100%',
											height: '100%',
											borderRadius: '50%',
											objectFit: 'cover'
										}}
									/>
								) : (
									<span className="text-white">
										{getFirstLetter(user?.name)}
									</span>
								)}
								
								{/* Upload overlay */}
								<div className="position-absolute" style={{
									bottom: '-5px',
									right: '-5px',
									background: '#70b452',
									borderRadius: '50%',
									width: '30px',
									height: '30px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									border: '2px solid white',
									cursor: 'pointer'
								}}>
									{uploadingPicture ? (
										<div
											className="spinner-border spinner-border-sm text-white"
											role="status"
											style={{ width: '12px', height: '12px' }}
										>
											<span className="visually-hidden">Loading...</span>
										</div>
									) : (
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="12"
											height="12"
											fill="white"
											className="bi bi-camera"
											viewBox="0 0 16 16"
										>
											<path d="M15 12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h1.172a3 3 0 0 0 2.12-.879l.83-.828A1 1 0 0 1 6.827 3h2.344a1 1 0 0 1 .707.293l.828.828A3 3 0 0 0 12.828 5H14a1 1 0 0 1 1 1zM2 4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1.172a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 9.172 2H6.828a2 2 0 0 0-1.414.586l-.828.828A2 2 0 0 1 3.172 4z"/>
											<path d="M8 11a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5m0 1a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M3 6.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0"/>
										</svg>
									)}
								</div>
							</div>

							{/* Hidden file input */}
							<input
								ref={fileInputRef}
								type="file"
								accept="image/*"
								onChange={handleProfilePictureChange}
								style={{ display: 'none' }}
							/>

							<h2 className="mb-1">{capitalizeFirstLetter(user?.name)}</h2>
							{/* <span className="">{user?.email}</span> */}
							<p className="center gap-2 mt-2">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="16"
									height="16"
									fill="#70b452"
									className="bi bi-patch-check-fill"
									viewBox="0 0 16 16"
								>
									<path d="M10.067.87a2.89 2.89 0 0 0-4.134 0l-.622.638-.89-.011a2.89 2.89 0 0 0-2.924 2.924l.01.89-.636.622a2.89 2.89 0 0 0 0 4.134l.637.622-.011.89a2.89 2.89 0 0 0 2.924 2.924l.89-.01.622.636a2.89 2.89 0 0 0 4.134 0l.622-.637.89.011a2.89 2.89 0 0 0 2.924-2.924l-.01-.89.636-.622a2.89 2.89 0 0 0 0-4.134l-.637-.622.011-.89a2.89 2.89 0 0 0-2.924-2.924l-.89.01zm.287 5.984-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7 8.793l2.646-2.647a.5.5 0 0 1 .708.708" />
								</svg>
								<span>
									Verified {user?.reseller === "yes" && "Reseller"} Account
									Holder
								</span>
							</p>
							{tab === 0 && (
								<button
									onClick={() => {
										setTab(1);
									}}
									className="update-button rounded-pill px-5 mt-3"
								>
									{loading ? (
										<div
											className="spinner-border spinner-border-sm"
											role="status"
										>
											<span className="visually-hidden"></span>
										</div>
									) : (
										"EDIT PROFILE"
									)}
								</button>
							)}
						</div>
						{tab === 1 && (
							<form className="account-form">
								<div
									className={`form-group ${(!form?.name || form?.name === "") && "redborder"
										}`}
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="16"
										height="16"
										fill="#747474 "
										className="bi bi-person-circle"
										viewBox="0 0 16 16"
									>
										<path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0" />
										<path
											fillRule="evenodd"
											d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8m8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1"
										/>
									</svg>
									<input
										value={form?.name}
										name="name"
										onChange={handleChange}
										type="text"
										placeholder="Name"
									/>
								</div>
								<div
									className={`form-group ${(!form?.email || form?.email === "") && "redborder"
										}`}
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="16"
										height="16"
										fill="#747474"
										className="bi bi-envelope"
										viewBox="0 0 16 16"
									>
										<path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1zm13 2.383-4.708 2.825L15 11.105zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741M1 11.105l4.708-2.897L1 5.383z" />
									</svg>
									<input
										value={form?.email}
										name="email"
										onChange={handleChange}
										type="email"
										placeholder="Email"
										readOnly={!!user?.email}
									/>
								</div>
								{user?.phone && (
									<div className="form-group">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="16"
											height="16"
											fill="#747474"
											className="bi bi-telephone"
											viewBox="0 0 16 16"
										>
											<path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.6 17.6 0 0 0 4.168 6.608 17.6 17.6 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.68.68 0 0 0-.58-.122l-2.19.547a1.75 1.75 0 0 1-1.657-.459L5.482 8.062a1.75 1.75 0 0 1-.46-1.657l.548-2.19a.68.68 0 0 0-.122-.58zM1.884.511a1.745 1.745 0 0 1 2.612.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.68.68 0 0 0 .178.643l2.457 2.457a.68.68 0 0 0 .644.178l2.189-.547a1.75 1.75 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.6 18.6 0 0 1-7.01-4.42 18.6 18.6 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877z" />
										</svg>
										<input
											value={user?.phone}
											name="phone"
											onChange={handleChange}
											type="tel"
											placeholder="Phone Number"
											required
											readOnly
										/>
									</div>
								)}



								{/* Password Change Fields */}
								{/* <div className="form-group">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="16"
										height="16"
										fill="#747474"
										className="bi bi-lock"
										viewBox="0 0 16 16"
									>
										<path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2m3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2M5 8h6a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1"/>
									</svg>
									<input
										value={form?.currentPassword || ""}
										name="currentPassword"
										onChange={handleChange}
										type="password"
										placeholder="Current Password (optional)"
									/>
								</div>
								
								<div className="form-group">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="16"
										height="16"
										fill="#747474"
										className="bi bi-key"
										viewBox="0 0 16 16"
									>
										<path d="M0 8a4 4 0 0 1 7.465-2H14a.5.5 0 0 1 .354.146l1.5 1.5a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0L13 9.207l-.646.647a.5.5 0 0 1-.708 0L11 9.207l-.646.647a.5.5 0 0 1-.708 0L9 9.207l-.646.647A.5.5 0 0 1 8 10h-.535A4 4 0 0 1 0 8m4-3a3 3 0 1 0 2.712 4.285A.5.5 0 0 1 7.163 9h.63l.853-.854a.5.5 0 0 1 .708 0l.646.647.646-.647a.5.5 0 0 1 .708 0l.646.647.646-.647a.5.5 0 0 1 .708 0l.646.647.793-.793-1-1h-6.63a.5.5 0 0 1-.451-.285A3 3 0 0 0 4 5"/>
										<path d="M4 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
									</svg>
									<input
										value={form?.newPassword || ""}
										name="newPassword"
										onChange={handleChange}
										type="password"
										placeholder="New Password (optional)"
									/>
								</div>

								{user?.sponsor && (
									<div className="form-group">
										<SponsorSvg />
										<span className="fw-bold">Sponsor: </span>
										<input
											onChange={handleChange}
											type="text"
											value={user?.sponsor}
										/>
									</div>
								)} */}
								<button
									onClick={handleUpdate}
									className="update-button rounded-pill"
									type="submit"
								>
									{loading ? (
										<div
											className="spinner-border spinner-border-sm"
											role="status"
										>
											<span className="visually-hidden"></span>
										</div>
									) : (
										"UPDATE PROFILE"
									)}
								</button>
							</form>
						)}
					</div>

					<div className="account-modal-bottom-content bg-transparent mt-3">
						<label
							htmlFor="marketingConsentToggleMobile"
							className="shadow-sm bg-white d-flex justify-content-between align-items-center rounded-5 px-2 py-2 my-2"
						>
							<div className="center gap-3">
								<div className="center content-item-left-icon-container shadow-sm rounded-pill flex-shrink-0">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="16"
										height="16"
										fill="#c9b800"
										className="bi bi-envelope-heart"
										viewBox="0 0 16 16"
									>
										<path fillRule="evenodd" d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v.217l3.235 1.94a2.8 2.8 0 0 0-.233 1.027L1 5.384v5.721l3.453-2.124q.219.416.55.835l-3.97 2.443A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741l-3.968-2.442q.33-.42.55-.836L15 11.105V5.383l-3.002 1.801a2.8 2.8 0 0 0-.233-1.026L15 4.217V4a1 1 0 0 0-1-1zm6 2.993c1.664-1.711 5.825 1.283 0 5.132-5.825-3.85-1.664-6.843 0-5.132" />
									</svg>
								</div>
								<div className="d-flex flex-column text-start lh-sm">
									<span className="content-item-name fw-bold">Email me about diamond deals and offers</span>
									<small className="text-muted">Unsubscribe anytime</small>
								</div>
							</div>
							<Switch
								id="marketingConsentToggleMobile"
								className="me-1 flex-shrink-0"
								checked={marketingConsent}
								loading={consentLoading}
								onChange={handleConsentChange}
							/>
						</label>
						<div
							className="shadow-sm bg-white d-flex justify-content-between align-items-center rounded-5 px-2 py-2 my-2"
							onClick={handleLogout}
						>
							<div className="center gap-3">
								<div className="center content-item-left-icon-container shadow-sm rounded-pill">
									<LogoutIcon className="icon" fill="#fff011" />
								</div>
								<span className="content-item-name text-danger fw-bold">
									Logout
								</span>
							</div>
							<div className="content-item-right-icon-container bg-light rounded-pill center me-1">
								<ChevronRightIcon />
							</div>
						</div>
					</div>
				</div>
			</div>

			<SideMenu sideMenu={sideMenu} setSideMenu={setSideMenu} />
		</>
	);
};

export default AccountModal;